/* eslint-disable no-misleading-character-class */
/* eslint-disable import/prefer-default-export */
import { walletTypes } from './constants';
import { getReadableFileSizeString } from './helpers';

export const validatePassword = (password, getRes = false) => {
  const CONTAINS_NUMBER = /[0-9]/;
  const CONTAINS_SPECIAL_CHAR = /[^\w\s]/;
  const CONTAINS_LETTERS = /[a-z]/;
  const CONTAINS_UPPER_CASE_LETTERS = /[A-Z]/;

  const isLongEnough = password.length >= 15;
  const hasNumber = CONTAINS_NUMBER.test(password);
  const hasSpecialCharacter = CONTAINS_SPECIAL_CHAR.test(password);
  const hasLowercaseLetter = CONTAINS_LETTERS.test(password);
  const hasUppercaseLetter = CONTAINS_UPPER_CASE_LETTERS.test(password);

  if (!getRes) {
    return (
      isLongEnough && hasUppercaseLetter && hasNumber && hasSpecialCharacter && hasLowercaseLetter
    );
  }

  return {
    isLongEnough,
    hasNumber,
    hasSpecialCharacter,
    hasLowercaseLetter,
    hasUppercaseLetter,
  };
};

export const validateSSN = (ssn) => {
  const cleanSSN = ssn.replaceAll('-', '');

  const CONTAINS_LETTERS = /[a-zA-Z]/;
  const CONTAINS_SPECIAL_CHAR = /[^0-9A-Za-z]/;

  const isLongEnough = cleanSSN.length === 9;
  const hasSpecialCharacter = CONTAINS_SPECIAL_CHAR.test(cleanSSN);
  const hasLetter = CONTAINS_LETTERS.test(cleanSSN);

  return isLongEnough && !hasSpecialCharacter && !hasLetter;
};

export const validatePIN = (pin) => {
  const ONLY_NUMBERS = /^[0-9\b]*$/;
  const CONTAINS_SPECIAL_CHAR = /[^0-9A-Za-z]/;

  const isLongEnough = pin.length === 4;
  const hasOnlyNumber = ONLY_NUMBERS.test(pin);
  const hasSpecialCharacter = CONTAINS_SPECIAL_CHAR.test(pin);

  return isLongEnough && hasOnlyNumber && !hasSpecialCharacter;
};

export const validateCVV = (cvv) => {
  const CONTAINS_SPECIAL_CHAR = /[^0-9A-Za-z]/;

  const isLongEnough = cvv.length === 3;
  const hasSpecialCharacter = !CONTAINS_SPECIAL_CHAR.test(cvv);

  return isLongEnough && hasSpecialCharacter;
};

export const validateEmail = (email) => {
  const r =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return r.test(String(email).toLowerCase());
};

export const validateEmailLocalPart = (email) => {
  const parts = email.split('@');
  if (parts.length < 2) return false;

  const localPart = parts[0];

  return localPart.length <= 64;
};

export const validateEmailSpecialChars = (email, allowedDomains) => {
  if (email.includes('+') && allowedDomains) {
    const domain = email.split('@')[1];
    const allowedDomainsList = allowedDomains.split(',').map((d) => d.trim());
    return allowedDomainsList.includes(domain);
  }
  return true;
};

export const validateLatinLettersOnly = (text, acceptEmptyString = false) => {
  const LATIN_LETTERS_ONLY = /^[A-Za-z\s]*$/;
  const isLongEnough = text && text.trim().length > 0 && text.trim().length < 25;
  const onlyValidLetters = LATIN_LETTERS_ONLY.test(text);
  return (!text && acceptEmptyString) || (onlyValidLetters && isLongEnough);
};

export const validatePhoneNumber = (text) => {
  const ONLY_NUMBERS = /^[0-9\b]*$/;
  return ONLY_NUMBERS.test(text);
};

export const validateInputLength = (text, textLength, withTrim = true) => {
  const isLongEnough = withTrim
    ? text && text.trim().length > 0 && text.trim().length <= textLength
    : text && text.length > 0 && text.length <= textLength;
  return isLongEnough;
};
export const ADDRESS_STREET_US_MIN_LENGTH = 4;
export const validateAddressChange = (text, isUs = false, name, onChange = true) => {
  const ALLOWED_CHARS = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9'.,&\s]+$/;
  const FIRST_CHAR_SPACE = /^\s/;
  const invalidWords = ['APO', 'PO', 'APOB', 'POB', 'OFFICE', 'CALL BOX', 'GPOBOX'];
  let addressDoesHasInvalidWords = true;
  let isValidAddressLength = true;
  if (isUs) {
    if (name === 'street') {
      if (onChange) {
        isValidAddressLength = text.length <= 40;
      } else {
        isValidAddressLength = text.length >= ADDRESS_STREET_US_MIN_LENGTH && text.length <= 40;
        addressDoesHasInvalidWords = !invalidWords.some((invalidWord) =>
          text.toUpperCase().startsWith(invalidWord.toUpperCase())
        );
      }
    } else {
      isValidAddressLength = text.length <= 30;
    }
  } else if (onChange) {
    isValidAddressLength = text.length <= 49;
  }

  const isFirstCharNotSpace = !FIRST_CHAR_SPACE.test(text);
  const isAllowedChars = ALLOWED_CHARS.test(text);

  return (
    isValidAddressLength && isFirstCharNotSpace && isAllowedChars && addressDoesHasInvalidWords
  );
};

export const validateNumbers = (num, type, allowComma = true) => {
  const ALLOWED_NUMBER_WITH_TWO_DECIMAL = allowComma
    ? /^\d*((.)|(.\d{0,2})?)$/
    : /^([\d]*)(\.\d{0,2})?$/;

  const ALLOWED_NUMBER_WITH_EIGHT_DECIMAL = allowComma
    ? /^\d*((.)|(.\d{0,8})?)$/
    : /^([\d]*)(\.\d{0,8})?$/;

  return type === walletTypes.CRYPTO
    ? ALLOWED_NUMBER_WITH_EIGHT_DECIMAL.test(num)
    : ALLOWED_NUMBER_WITH_TWO_DECIMAL.test(num);
};

export const validateFileSize = (size, allowedSize, unit) => {
  const byteUnits = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const readableSizeValue = getReadableFileSizeString(size);

  const firstSizeUnitIndex = byteUnits.indexOf(unit);
  const secondSizeUnitIndex = byteUnits.indexOf(readableSizeValue?.unit);

  if (secondSizeUnitIndex > firstSizeUnitIndex) {
    return false;
  }
  if (secondSizeUnitIndex < firstSizeUnitIndex) {
    return true;
  }
  return readableSizeValue.number <= allowedSize;
};

export const validateMultiFileSizes = (files, allowedSize, unit) => {
  if (files && files?.length > 0) {
    const notAllowedFileSize = files?.find(
      (file) => !validateFileSize(file?.size, allowedSize, unit)
    );
    if (notAllowedFileSize) return false;
    return true;
  }
  return true;
};
